import { useCallback, useEffect, useState } from 'react';
import { scheduleService } from '@/services/schedule.service';
import type { EventRow } from '@/lib/schedule';

export interface UseOngoingEventsResult {
  /** Unfinished events for the household — not yet filtered to multi-day. */
  events: EventRow[];
  loading: boolean;
  /** Re-read the pool. Call it wherever the day feed's `refresh()` is called. */
  refresh: () => Promise<void>;
}

/**
 * The household's pool of unfinished events, for the "All ongoing" section and
 * the pinned span bars (YAR-419).
 *
 * Keyed on `todayKey` rather than read from the clock, so a tab left open
 * across midnight re-fetches and "Ends today" stops lying — the labels are
 * derived from that same key downstream.
 *
 * A failed read resolves to an empty list instead of throwing: the section has
 * no error UI of its own (AC 19), and letting this reject would take the whole
 * day feed down over a surface that is meant to be supplementary.
 */
export function useOngoingEvents(
  householdId: string | null | undefined,
  todayKey: string,
  userTimezone?: string | null,
): UseOngoingEventsResult {
  const [events, setEvents] = useState<EventRow[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(
    async (isCancelled?: () => boolean) => {
      if (!householdId) {
        setEvents([]);
        return;
      }
      setLoading(true);
      try {
        const rows = await scheduleService.getOngoingEvents(
          householdId,
          todayKey,
          userTimezone,
        );
        if (!isCancelled?.()) setEvents(rows as unknown as EventRow[]);
      } catch (err) {
        console.error('Failed to load ongoing events:', err);
        if (!isCancelled?.()) setEvents([]);
      } finally {
        if (!isCancelled?.()) setLoading(false);
      }
    },
    [householdId, todayKey, userTimezone],
  );

  useEffect(() => {
    let cancelled = false;
    void load(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [load]);

  // Exposed so create / edit / delete can re-read the pool. Without it the
  // section keeps serving the rows it fetched on mount, and a multi-day event
  // the parent just created does not appear until the tab remounts — the day
  // feed's own `refresh()` does not touch this query.
  const refresh = useCallback(() => load(), [load]);

  return { events, loading, refresh };
}
